import { usePostHog } from '@posthog/react'
import { format } from 'date-fns'
import { X } from 'lucide-react'

import { cn } from '@/utils/ui/styles'

import { parseUTCToLocalDate } from '@/shared/datetime/utils/format'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { useCaseFilters } from '../../hooks/use-case-filters'
import {
	caseFilterValueLabel,
	visibleCaseFilterGroups,
} from '../../lib/case-filter-groups'

import type { CaseFilterGroup } from '../../lib/case-filter-groups'
import type { CaseDateField } from '../../lib/case-filters'
import type { CaseListAnalyticsPrefix } from '../../lib/case-list-analytics'

type CaseFilterChipsProps = {
	analyticsPrefix: CaseListAnalyticsPrefix
	/**
	 * Whether the Investigation group is on screen. A hidden group does not
	 * narrow the list, so it gets no chip either.
	 */
	showInvestigation: boolean
	className?: string
}

type Chip = {
	key: string
	text: string
	onRemove: () => void
}

/** Format an ISO URL date for a chip, tolerating a malformed value. */
const formatDate = (value: string | undefined) => {
	if (!value) return null
	try {
		return format(parseUTCToLocalDate(value), 'MMM d, yyyy')
	} catch {
		return null
	}
}

/**
 * The active Case filters as removable chips — one per selected value, then
 * the dates — with Clear all. Pure URL state, so the page skeleton renders it
 * for real and the chips do not pop in when the rows land.
 *
 * No start date means all time (`toCaseListParams`), so there is no chip for
 * it.
 */
export const CaseFilterChips = ({
	analyticsPrefix,
	showInvestigation,
	className,
}: CaseFilterChipsProps) => {
	const posthog = usePostHog()
	const { state, filters, catalog, toggle, setDate, clearAll } =
		useCaseFilters()
	const { openedAfter, openedBefore } = state

	const removeDate = (field: CaseDateField) => {
		setDate(field, undefined)
		posthog.capture(`${analyticsPrefix}_date_filter`, {
			action: 'clear_badge',
			field,
		})
	}

	// Source has no options: its name comes from the catalog.
	const valueLabel = ({ options }: CaseFilterGroup, value: string) =>
		options === null
			? (catalog.get(value)?.name ?? value)
			: caseFilterValueLabel(options, value)

	const chips: Array<Chip> = visibleCaseFilterGroups(showInvestigation).flatMap(
		(group) => {
			// Widened on purpose: a union of value arrays has no callable `map`.
			const values: ReadonlyArray<string> = filters[group.key] ?? []
			return values.map((value) => ({
				key: `${group.key}:${value}`,
				text: `${group.label}: ${valueLabel(group, value)}`,
				onRemove: () => toggle(group.key, value),
			}))
		},
	)

	const formattedAfter = formatDate(openedAfter)
	const formattedBefore = formatDate(openedBefore)
	if (formattedAfter) {
		chips.push({
			key: 'openedAfter',
			text: `Opened after: ${formattedAfter}`,
			onRemove: () => removeDate('openedAfter'),
		})
	}
	if (formattedBefore) {
		chips.push({
			key: 'openedBefore',
			text: `Opened before: ${formattedBefore}`,
			onRemove: () => removeDate('openedBefore'),
		})
	}

	if (chips.length === 0) return null

	return (
		<div
			className={cn(
				'flex min-h-4 w-full flex-row flex-wrap items-center gap-2',
				className,
			)}
		>
			<p className="text-muted-foreground text-sm">Filters:</p>

			{chips.map((chip) => (
				<Badge key={chip.key} className="flex flex-row items-center gap-2">
					{chip.text}
					<Button
						variant="ghost"
						size="icon"
						className="size-4"
						onClick={chip.onRemove}
						title={`Remove ${chip.text}`}
					>
						<X className="size-4" />
					</Button>
				</Badge>
			))}

			<Button variant="ghost" size="sm" onClick={clearAll}>
				Clear all
			</Button>
		</div>
	)
}
